// PenSettingsPanel — 描画ツール挙動設定 (手ぶれ補正 / アンチエイリアス /
// ブラシカーソル / スポイトの取得元) の **自己完結パネル部品**
// (consult 2026-06-13 続報、modern-layout.md §1.1 表現パリティ)。
//
// 2026-06-27 (consult): 旧 UserPreferencesModal「表示の設定」に同居していた描画
// ツール挙動 (ブラシカーソル / スポイトの取得元) を本パネルへ集約した。「表示」
// モーダルは純表示 (サイドバー / チャット文字) のみに戻り、本パネル側が手ぶれ補正・
// AA・ブラシカーソル・スポイトを束ねる「描画」モーダルの中身になる (= 上部バー
// ボタンも「ペン」→「描画」へ改名)。値の真実 (共有 chrome) と挙動は不変、置き場所
// と配線だけの変更。
//
// 経緯: 初版 (2026-06-13) は UserPreferencesModal「ペン詳細」セクションに同居させたが、
// consult 2026-06-13 続報で **発見性のため独立** させた: 上部バーに「ペン」ボタンを
// 新設し、本パネルを載せた専用モーダル (`PenSettingsModal`) から開く。配置・モーダルの
// 作り・state 管理は「ショートカット」ボタン + `ShortcutSettingsPanel` の前例を踏襲。
//
// 設計意図 (二段構え、ShortcutSettingsPanel と同流儀): 本 component はモーダル外枠と
// **疎結合** な self-contained 部品 (= 外枠 chrome / Esc / backdrop は呼び出し側
// `PenSettingsModal` が持ち、本体は中身だけ描く)。将来「設定」タブ shell に畳むときも
// タブ body にそのまま再マウントできる。
//
// **将来の拡張前提**: β 予定の筆圧カーブ本格 UI (CSP 風カーブエディタ、
// `PenPressureConfig.tsx` ヘッダ参照) 等、ペンの描き味設定はこのパネルが受け皿になる。
//
// 値のモデル (= UserPreferencesModal 時代から不変):
//   - **共有 chrome の tri-state 実効値** (useRoomChrome) を controlled で読み書きする
//     だけ。永続化 (oekaki:stabilizer-level / oekaki:aa-enabled) は chrome 側が担う。
//   - ModernToolProperties の手ぶれ補正/AA トグルと **同じ単一 source** (= 同じ共有設定の
//     別ビュー、二重の真実にしない)。レイアウト跨ぎで共有、未設定の共通既定は off
//     (= 旧 Java 互換の描き味、consult 2026-06-13 続報でレイアウト別既定を撤廃)。
//
// UI は UserPreferencesModal の <select> パターン (legend + プルダウン) を踏襲。表示語は
// ModernToolProperties と同じ「なし/弱/中/強」「なし/あり」(= 表示語も揃える)。

import { useId } from 'react';

import {
  type BrushCursorType,
  isBrushCursorType,
} from '../infrastructure/brushCursorPreference.js';
import { isDotByDotMode } from '../infrastructure/dotByDotPreference.js';
import type { DotByDotMode } from '../infrastructure/drawing/zoom.js';
import {
  isSpuitSampleMode,
  type SpuitSampleMode,
} from '../infrastructure/spuitSamplePreference.js';
import { isStabilizerLevel, type StabilizerLevel } from '../infrastructure/stabilizerPreference.js';
import { SettingCheckboxRow } from './layouts/shared/SettingCheckboxRow.js';

export interface PenSettingsPanelProps {
  /** 手ぶれ補正レベルの **実効値** (tri-state 解決済み、controlled = chrome.stabilizerLevel)。 */
  readonly stabilizerLevel: StabilizerLevel;
  /** 手ぶれ補正変更時. chrome (onStabilizerChange) が明示設定として永続 + engine 反映. */
  readonly onStabilizerChange: (level: StabilizerLevel) => void;
  /** アンチエイリアス (AA) の **実効値** (tri-state 解決済み、controlled = chrome.aaEnabled)。 */
  readonly aaEnabled: boolean;
  /** AA 変更時. chrome (onAaChange) が明示設定として永続 + pen.aa 同期. */
  readonly onAaChange: (next: boolean) => void;
  /** ドットバイドット表示の設定 ('auto' | 'off'、controlled = chrome.dotByDotMode)。 */
  readonly dotByDotMode: DotByDotMode;
  readonly onDotByDotModeChange: (next: DotByDotMode) => void;
  /** この画面 (拡大率) で自動が効く対象か。説明の一言に使う。 */
  readonly dotByDotActive: boolean;
  /**
   * 現在のブラシカーソル種別 (controlled、consult 2026-06-21)。'brush-ring' =
   * ブラシ円リング表示、'arrow' = OS 標準矢印カーソル. 共有 chrome が持つので
   * Classic/Modern どちらの「描画」設定からも同じ値を読み書きする。
   * 2026-06-27 で「表示」モーダルから本パネルへ移設。
   */
  readonly brushCursorType: BrushCursorType;
  /** ブラシカーソル種別変更時. 親 (chrome) 側で setState + localStorage 書き込み. */
  readonly onBrushCursorTypeChange: (type: BrushCursorType) => void;
  /**
   * 自分の確定済みカスタムカーソルを自分の画面にも表示するか (controlled、作者
   * staging フィードバック 2026-09-19 follow-up)。既定 ON。OFF は自分の画面での
   * 表示だけに影響し、相手への配信・相手側の表示・参加者リストのサムネイルには
   * 影響しない。共有 chrome が持つので Classic/Modern どちらの「描画」設定 /
   * `CursorEditorPanel` 下部のチェックボックスからも同じ値を読み書きする。
   */
  readonly showOwnCustomCursor: boolean;
  /** 変更時. 親 (chrome) 側で setState + localStorage 書き込み. */
  readonly onShowOwnCustomCursorChange: (next: boolean) => void;
  /**
   * 現在のスポイトのサンプル元モード (controlled、spuit-sample-modes-2026-06-22)。
   * A 'display' (既定) / B 'layer' / C 'layer-bg'. 共有 chrome が持つので Classic/Modern
   * どちらの「描画」設定からも同じ値を読み書きする。2026-06-27 で「表示」モーダルから本パネルへ移設。
   */
  readonly spuitSampleMode: SpuitSampleMode;
  /** スポイトのサンプル元モード変更時. 親 (chrome) 側で setState + localStorage 書き込み. */
  readonly onSpuitSampleModeChange: (mode: SpuitSampleMode) => void;
}

// 手ぶれ補正の選択肢。値は engine の StabilizerLevel enum、ラベルは
// ModernToolProperties と同じ「なし/弱/中/強」(= 二重の真実を作らない)。
const STABILIZER_OPTIONS: ReadonlyArray<{
  readonly value: StabilizerLevel;
  readonly label: string;
}> = [
  { value: 'off', label: 'なし' },
  { value: 'weak', label: '弱' },
  { value: 'medium', label: '中' },
  { value: 'strong', label: '強' },
] as const;

// AA の on/off。<select> で受けるため boolean を 'off' | 'on' に encode する。
// ラベルは ModernToolProperties と同じ「なし/あり」。
const AA_OPTIONS: ReadonlyArray<{ readonly value: 'off' | 'on'; readonly label: string }> = [
  { value: 'off', label: 'なし' },
  { value: 'on', label: 'あり' },
] as const;

// ドットバイドット表示の選択肢 (既定 '自動' を先頭)。
const DOT_BY_DOT_OPTIONS: ReadonlyArray<{ readonly value: DotByDotMode; readonly label: string }> =
  [
    { value: 'auto', label: '自動' },
    { value: 'off', label: 'しない' },
  ] as const;

export const DOT_BY_DOT_DESCRIPTION =
  '画面の拡大率が 125%・150% などのとき、絵の 1 ドットを画面の 1 ドットで表示します (絵は少し小さく見えます)。';

/** 設定 × 今の画面 (対象か) から出す一言. */
export const dotByDotStatusText = (mode: DotByDotMode, active: boolean): string => {
  if (!active) return 'この画面では変わりません。';
  return mode === 'auto' ? 'この画面では効いています。' : 'この画面では「しない」にしています。';
};

// ブラシカーソル種別の選択肢 (consult 2026-06-21、2026-06-27 で本パネルへ移設)。
// 既定 (ブラシ円) を先頭に置く。
//   - ブラシ円: ペンが描ける範囲を示すリング (= 現状維持)。
//   - 矢印: OS 標準カーソル. リングを出さず絵を一切隠さない (= 旧 Java 版の懐古)。
const BRUSH_CURSOR_TYPE_OPTIONS: ReadonlyArray<{
  readonly value: BrushCursorType;
  readonly label: string;
}> = [
  { value: 'brush-ring', label: 'ブラシ円' },
  { value: 'arrow', label: '矢印' },
] as const;

// スポイトのサンプル元モードの選択肢 (spuit-sample-modes-2026-06-22 §2、
// 2026-06-27 で本パネルへ移設)。既定 (A 表示色) を先頭に置く。ラベルは設計書 §2 の文言。
//   - A 'display'  … 全可視レイヤー + bg を合成した「見たままの色」(CSP/PS 標準)。
//   - B 'layer'    … 選択レイヤーの生ピクセル (bg 無視)。透明部分は消しゴムへ切替。
//   - C 'layer-bg' … 選択レイヤー + 背景色 (旧来挙動)。
const SPUIT_SAMPLE_MODE_OPTIONS: ReadonlyArray<{
  readonly value: SpuitSampleMode;
  readonly label: string;
}> = [
  { value: 'display', label: '表示色を取得（全て合成）' },
  { value: 'layer', label: 'レイヤーから取得' },
  { value: 'layer-bg', label: 'レイヤーと背景色から取得' },
] as const;

/**
 * Pure UI 部分 (hooks を使わない). props ですべてが決まる function なので
 * テストでは `PenSettingsPanelView({...})` を直接呼んで React tree を走査できる
 * (= UserPreferencesModal.test.ts と同流儀)。外側の `PenSettingsPanel` で useId を
 * 捌き、legend ID を本コンポーネントに props で渡す.
 */
export interface PenSettingsPanelViewProps extends PenSettingsPanelProps {
  /** 手ぶれ補正 <select> の label 紐付け (aria-labelledby) 用 ID. */
  readonly stabilizerLegendId: string;
  /** アンチエイリアス <select> の label 紐付け (aria-labelledby) 用 ID. */
  readonly aaLegendId: string;
  /** ドットバイドット <select> の label 紐付け (aria-labelledby) 用 ID. */
  readonly dotByDotLegendId: string;
  /** ブラシカーソル <select> の label 紐付け (aria-labelledby) 用 ID. */
  readonly brushCursorLegendId: string;
  /** スポイト サンプル元 <select> の label 紐付け (aria-labelledby) 用 ID. */
  readonly spuitSampleLegendId: string;
}

export const PenSettingsPanelView = ({
  stabilizerLevel,
  onStabilizerChange,
  aaEnabled,
  onAaChange,
  dotByDotMode,
  onDotByDotModeChange,
  dotByDotActive,
  brushCursorType,
  onBrushCursorTypeChange,
  showOwnCustomCursor,
  onShowOwnCustomCursorChange,
  spuitSampleMode,
  onSpuitSampleModeChange,
  stabilizerLegendId,
  aaLegendId,
  dotByDotLegendId,
  brushCursorLegendId,
  spuitSampleLegendId,
}: PenSettingsPanelViewProps): React.ReactNode => (
  <div>
    {/* 値は chrome 共有 (Classic / Modern 同一)。線そのものに効く設定なので、
        UserPreferencesModal の「他の参加者には影響しません」文言は使わない
        (= AA はあなたの線の見た目としてキャンバスに乗り、全員に見える)。 */}
    <p className="mb-4 text-[11px] text-[var(--color-text-muted)]">
      この端末でのみ保存される個人設定です。Classic / Modern 共通で使われます。
    </p>
    <StabilizerSelect
      legend="手ぶれ補正"
      legendId={stabilizerLegendId}
      value={stabilizerLevel}
      onChange={onStabilizerChange}
    />
    <p className="mt-1 mb-3 text-[11px] text-[var(--color-text-muted)]">
      線のふるえを補正します。「なし」で旧版と同じ描き味です。
    </p>
    <AaSelect
      legend="アンチエイリアス"
      legendId={aaLegendId}
      value={aaEnabled}
      onChange={onAaChange}
    />
    <p className="mt-1 mb-3 text-[11px] text-[var(--color-text-muted)]">
      線のふちを滑らかにします。「なし」で旧版と同じ描き味です。
    </p>
    {/* ドットバイドット表示 (canvas-dot-by-dot-2026-10-28.md): AA と同じ select の見た目。 */}
    <DotByDotSelect
      legend="ドットバイドット表示"
      legendId={dotByDotLegendId}
      value={dotByDotMode}
      onChange={onDotByDotModeChange}
    />
    <p className="mt-1 mb-3 text-[11px] text-[var(--color-text-muted)]">
      {DOT_BY_DOT_DESCRIPTION}
      {dotByDotStatusText(dotByDotMode, dotByDotActive)}
    </p>
    {/* 2026-06-27 (consult): 旧「表示」モーダルから移設したブラシカーソル種別。
        手ぶれ補正 / AA と同じ描画ツール挙動として並べる。'ブラシ円' (既定) は描ける
        範囲のリング、'矢印' は OS 標準カーソルで絵を一切隠さない (= 旧 Java 版の懐古)。 */}
    <BrushCursorTypeSelect
      legend="ブラシカーソル"
      legendId={brushCursorLegendId}
      value={brushCursorType}
      onChange={onBrushCursorTypeChange}
    />
    <p className="mt-1 mb-3 text-[11px] text-[var(--color-text-muted)]">
      ペン先のカーソル表示です。「矢印」で絵を隠さず描けます。
    </p>
    {/* 作者 staging 確認フィードバック 2026-09-19 follow-up4: スイッチが文言と離れて
        分かりづらい → チェックボックスに戻し、ラベル文字を強調 (`CursorEditorPanel`
        下部の同じ行と見た目を揃える、共有する controlled 値)。ブラシカーソル項の直下
        という位置は変えない。 */}
    <div className="mt-1 mb-3">
      <SettingCheckboxRow
        label="自分の画面にも表示"
        description="OFF にすると自分の画面からだけ消えます。相手には見えたままです"
        checked={showOwnCustomCursor}
        onChange={onShowOwnCustomCursorChange}
      />
    </div>
    {/* 2026-06-27 (consult): 旧「表示」モーダルから移設したスポイトの取得元。
        既定 A (表示色) は CSP/PS 標準の「見たままの色」。共有 chrome で Classic/Modern 共通。 */}
    <SpuitSampleModeSelect
      legend="スポイトの取得元"
      legendId={spuitSampleLegendId}
      value={spuitSampleMode}
      onChange={onSpuitSampleModeChange}
    />
  </div>
);

export const PenSettingsPanel = (props: PenSettingsPanelProps): React.ReactNode => {
  const stabilizerLegendId = useId();
  const aaLegendId = useId();
  const dotByDotLegendId = useId();
  const brushCursorLegendId = useId();
  const spuitSampleLegendId = useId();
  return (
    <PenSettingsPanelView
      {...props}
      stabilizerLegendId={stabilizerLegendId}
      aaLegendId={aaLegendId}
      dotByDotLegendId={dotByDotLegendId}
      brushCursorLegendId={brushCursorLegendId}
      spuitSampleLegendId={spuitSampleLegendId}
    />
  );
};

/**
 * 手ぶれ補正レベルの <select>. UI は UserPreferencesModal の select 群と揃える。
 * <select> の値は option 由来だが、防御的に isStabilizerLevel guard で narrow して
 * から伝搬する。
 */
const StabilizerSelect = ({
  legend,
  legendId,
  value,
  onChange,
}: {
  readonly legend: string;
  readonly legendId: string;
  readonly value: StabilizerLevel;
  readonly onChange: (next: StabilizerLevel) => void;
}): React.ReactNode => (
  <div className="flex items-center justify-between gap-2">
    <span id={legendId} className="text-xs font-bold text-[var(--color-text)]">
      {legend}
    </span>
    <select
      aria-labelledby={legendId}
      value={value}
      onChange={(e) => {
        if (isStabilizerLevel(e.target.value)) onChange(e.target.value);
      }}
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs text-[var(--color-text)]"
    >
      {STABILIZER_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

/**
 * アンチエイリアス (AA) の on/off <select>. boolean ⇄ 'on' | 'off' を encode/decode
 * して controlled に保つ。
 */
const AaSelect = ({
  legend,
  legendId,
  value,
  onChange,
}: {
  readonly legend: string;
  readonly legendId: string;
  readonly value: boolean;
  readonly onChange: (next: boolean) => void;
}): React.ReactNode => (
  <div className="flex items-center justify-between gap-2">
    <span id={legendId} className="text-xs font-bold text-[var(--color-text)]">
      {legend}
    </span>
    <select
      aria-labelledby={legendId}
      value={value ? 'on' : 'off'}
      onChange={(e) => {
        onChange(e.target.value === 'on');
      }}
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs text-[var(--color-text)]"
    >
      {AA_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

/** ドットバイドット表示の <select> (AaSelect と同じ見た目). */
const DotByDotSelect = ({
  legend,
  legendId,
  value,
  onChange,
}: {
  readonly legend: string;
  readonly legendId: string;
  readonly value: DotByDotMode;
  readonly onChange: (next: DotByDotMode) => void;
}): React.ReactNode => (
  <div className="flex items-center justify-between gap-2">
    <span id={legendId} className="text-xs font-bold text-[var(--color-text)]">
      {legend}
    </span>
    <select
      aria-labelledby={legendId}
      value={value}
      onChange={(e) => {
        if (isDotByDotMode(e.target.value)) onChange(e.target.value);
      }}
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs text-[var(--color-text)]"
    >
      {DOT_BY_DOT_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

/**
 * ブラシカーソル種別の <select> (BrushCursorType 用、consult 2026-06-21、
 * 2026-06-27 で本パネルへ移設)。UI は他の select 群と揃える。<select> の値は option
 * 由来だが、防御的に isBrushCursorType guard で narrow してから伝搬する。
 */
const BrushCursorTypeSelect = ({
  legend,
  legendId,
  value,
  onChange,
}: {
  readonly legend: string;
  readonly legendId: string;
  readonly value: BrushCursorType;
  readonly onChange: (next: BrushCursorType) => void;
}): React.ReactNode => (
  <div className="flex items-center justify-between gap-2">
    <span id={legendId} className="text-xs font-bold text-[var(--color-text)]">
      {legend}
    </span>
    <select
      aria-labelledby={legendId}
      value={value}
      onChange={(e) => {
        if (isBrushCursorType(e.target.value)) onChange(e.target.value);
      }}
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs text-[var(--color-text)]"
    >
      {BRUSH_CURSOR_TYPE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

/**
 * スポイトのサンプル元モードの <select> (SpuitSampleMode 用、spuit-sample-modes-2026-06-22、
 * 2026-06-27 で本パネルへ移設)。ラベルが長い (= 設計書 §2 の文言) ので幅は自動。
 * 防御的に isSpuitSampleMode guard で narrow してから伝搬する。
 */
const SpuitSampleModeSelect = ({
  legend,
  legendId,
  value,
  onChange,
}: {
  readonly legend: string;
  readonly legendId: string;
  readonly value: SpuitSampleMode;
  readonly onChange: (next: SpuitSampleMode) => void;
}): React.ReactNode => (
  <div className="flex items-center justify-between gap-2">
    <span id={legendId} className="shrink-0 text-xs font-bold text-[var(--color-text)]">
      {legend}
    </span>
    <select
      aria-labelledby={legendId}
      value={value}
      onChange={(e) => {
        if (isSpuitSampleMode(e.target.value)) onChange(e.target.value);
      }}
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs text-[var(--color-text)]"
    >
      {SPUIT_SAMPLE_MODE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);
